import {Component, OnDestroy, OnInit} from '@angular/core';
import {BreadcrumbModel} from '@shared/models/breadcrumb.model';
import {take, takeUntil} from 'rxjs/operators';
import {select, Store} from '@ngrx/store';
import {ActivatedRoute, Router} from '@angular/router';
import {Subject} from 'rxjs';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {DialogService} from '@shared/services/dialog-service';
import {memoTitle, memoToolId} from '../../orbitax-excel-memo.constants';
import {
  CreateEmptyMemoCommand,
  DownloadExcelMemoReportCommand,
  UpdateMemoCommand
} from '../../orbitax-excel-memo.commands';
import {CommandService} from '@core/services/command.service';
import {Configuration} from '@shared/models/configuration';
import {FileManagementService} from '@core/services/file-management.service';
import { FuseSplashScreenService } from '@barebone-fuse-services/splash-screen.service';
import { FuseSidebarService } from '@barebone-fuse/@fuse/components/sidebar/sidebar.service';
import {OrbitaxExcelMemoService} from '../../orbitax-excel-memo.service';
import {getApplicationWithId, ParticleState} from '@shared/store/reducers/particle.reduer';
import {PlatformSidebarService} from "@shared/components/platform-sidebar/store/platform-sidebar.service";
import {PlatformSidebarState} from "@shared/components/platform-sidebar/store/platform-sidebar.reducer";
import {
  SetActivityFeedAction,
  SetCurrentRightSideBarFoldOrNotAction,
  SetCurrentRightSideComponentAction
} from "@shared/components/platform-sidebar/store/platform-sidebar.action";

@Component({
    standalone: false,
    selector: 'particle-excel-memo-content',
  templateUrl: './excel-memo-content.component.html',
  styleUrls: ['./excel-memo-content.component.scss']
})
export class ExcelMemoContentComponent implements OnInit, OnDestroy {
  appUrl = '';
  breadcrumbs: BreadcrumbModel[];
  unsubscribe$ = new Subject<void>();
  private memoType: string;
  sourceKey: string;
  invalidateSubject: InvalidateSubject;
  public isGridReady = false;
  memoCount: number;

  constructor(private store: Store<ParticleState>,
              private activatedRoute: ActivatedRoute,
              private dialogService: DialogService,
              private router: Router,
              private commandService: CommandService,
              private fileManagementService: FileManagementService,
              private fuseSplashScreenService: FuseSplashScreenService,
              private excelMemoService: OrbitaxExcelMemoService,
              private platformSidebarStore: Store<PlatformSidebarState>,
              private platformSidebarService: PlatformSidebarService,
              private sidebarService: FuseSidebarService
  ) {
    this.sourceKey = 'Memo-Template';
    this.invalidateSubject = new InvalidateSubject();
  }

  ngOnInit() {
    this.store.pipe(select(getApplicationWithId, 'ITCE')).subscribe(app => {
      if (app && app.HostUrl) {
        this.appUrl = app.HostUrl;
      }
    });
    const config = Configuration.getSmartGridConfig(this.sourceKey);
    this.activatedRoute.params.pipe(takeUntil(this.unsubscribe$)).subscribe((params: any) => {
      if (params && params.memoType) {
        this.excelMemoService.getExcelMemoCount(params.memoType).subscribe(count => {
          this.memoCount = count;
          if (this.memoCount > 0) {
            this.isGridReady = true;
          }
        }, err => {
          console.log(err);
          this.isGridReady = true;
        });

        this.memoType = params.memoType;
        config.Parameters['templateType'] = this.memoType;
        this.invalidateSubject.invalidate();
        this.addBreadCrumb();
      }
    });
    // const activityInfo = {
    //   type: 'Tool',
    //   'id': memoToolId[this.memoType],
    //   'title': memoTitle[this.memoType],
    //   isDefault: true
    // };
    // this.platformSidebarStore.dispatch(new SetActivityFeedAction(activityInfo));
  }

  private addBreadCrumb() {
    this.breadcrumbs = [
      <BreadcrumbModel>{
        id: 'planning',
        title: 'Planning',
        type: 'Planning',
        relativeUrl: `planning/${this.memoType}`
      },
      <BreadcrumbModel>{
        id: 'memoType',
        title: memoTitle[this.memoType],
        type: 'memoType',
        relativeUrl: `planning/${this.memoType}`
      }
    ];
  }

  createMemo() {
    this.fuseSplashScreenService.show();
    const command = new CreateEmptyMemoCommand(this.memoType, '');
    this.commandService.executeCommand(command).pipe().subscribe(data => {
      this.fuseSplashScreenService.hide();
      this.router.navigate([data.Payload.Id], {relativeTo: this.activatedRoute});
    });
  }

  onGridCellValueChange(event: any) {
    if (event) {
      if (!event.action && event.fieldDefinition.key === 'MemoCode') {
        console.log(event);
        this.router.navigate([event.item.id], {relativeTo: this.activatedRoute});
      }
      if (event.action) {
        if (event.action.id === 'Open') {
          this.router.navigate([event.item.id], {relativeTo: this.activatedRoute});
        } else if (event.action.id === 'View') {
          this.router.navigate([event.item.id, 'view'], {relativeTo: this.activatedRoute});
        } else if (event.action.id === 'Edit') {
          if (event.fieldDefinition.key === 'MemoCode') {
            this.openRenameDialog(event);
          } else {
            this.openEditor(event);
          }
        } else if (event.action.id === 'Download') {
          this.fuseSplashScreenService.show();
          this.commandService.executeCommand(new DownloadExcelMemoReportCommand(event.item.id)).subscribe(response => {
            this.fileManagementService.downloadClientFileWithId(response.Payload).subscribe(payload => {
              this.fuseSplashScreenService.hide();
              this.fileManagementService.openSaveFileWindow(payload, `${this.memoType}MemoReport.xlsx`);
            });
          });
        } else if (event.action.id === 'DownloadPdf') {
          this.fuseSplashScreenService.show();
          this.excelMemoService.downloadMemo(event.item.id, this.memoType).subscribe(file => {
            this.fuseSplashScreenService.hide();
            const url = window.URL.createObjectURL(file);
            window.open(url);

          });
        } else if (event.action.id === 'ShowActivity') {
          const referenceCode = event.item.cells['ReferenceCode'].value;
          const referenceTitle = event.item.cells['ReferenceTitle'].displayText;
          const activityInfo = {type: 'ReferenceCode', 'id': referenceCode, 'title': referenceTitle, isDefault: false, isUserAction: true};
          this.platformSidebarStore.dispatch(new SetActivityFeedAction(activityInfo));
        }
      }
    }
  }

  private openRenameDialog(event: any) {
    const existingTitle = event.item.cells.MemoCode.displayText;
    this.dialogService
      .showTextInputDialog(
        'Rename Memo Code',
        existingTitle,
        '',
        50,
        'Memo Code'
      )
      .pipe(take(1))
      .subscribe((state) => {
        if (state.action === 'OK') {
          const item = {id: event.item.id, code: state.data.input.text, template: this.memoType};
          const command = new UpdateMemoCommand(item);
          this.commandService.executeCommand(command).pipe().subscribe(commandState => {
            this.invalidateSubject.invalidate();
          });
        }
      });
  }

  private openEditor(event: any) {
    const minWidth = '800px';
    const maxWidth = '800px';

    this.dialogService.showCellEditorDialog(event.item, event.fieldDefinition, minWidth, maxWidth, this.sourceKey).subscribe(state => {
      if (state.action === 'OK') {
        const item = {id: event.item.id, title: state.data.input.item.cells.MemoName.value};
        const command = new UpdateMemoCommand(item);
        this.commandService.executeCommand(command).pipe().subscribe(commandState => {
          this.invalidateSubject.invalidate();
        });
      }
    });
  }

  ngOnDestroy(): void {
    if (this.sidebarService.getSidebar('rightsidebar')) {
      this.sidebarService.getSidebar('rightsidebar').close();
      this.sidebarService.getSidebar('rightsidebar').fold();
    }
    this.platformSidebarService.hideSidebarIcon("Related Law Changes");
    this.platformSidebarStore.dispatch(new SetCurrentRightSideComponentAction(null));
    this.platformSidebarStore.dispatch(new SetCurrentRightSideBarFoldOrNotAction(true));
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
